import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { FilterSettings } from '@/components/Icons/FilterSettings';
import { Icon } from '@/components/Icons';
import { RatingsEmptyCard } from '@/pages/Overview/RatingsEmptyCard';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { Tooltip } from '@/components/Tooltip';
import { useCloseOnBreakpointChange } from '@/hooks/useCloseOnBreakpointChange';
import { useIsMobile } from '@/hooks/useIsMobile';
import { CompareChip } from '@/pages/Overview/CompareChip';
import { EvaluationTypePicker } from '@/pages/Overview/EvaluationTypePicker';
import {
  RatingsFilterSheet,
  type RatingsFilterValues,
} from '@/pages/Overview/RatingsFilterSheet';
import type { RatingsFilters } from '@/pages/Overview/hooks/useEvaluationRatings';
import { useEvaluationRatings } from '@/pages/Overview/hooks/useEvaluationRatings';
import { useStudyYearPeriods } from '@/pages/Overview/hooks/useStudyYearPeriods';
import { PeriodPicker } from '@/pages/Overview/PeriodPicker';
import {
  findChartByType,
  getRankingChart,
  getRankingSubjects,
} from '@/pages/Overview/ratingsChart';
import { RatingsSectionTabs } from '@/pages/Overview/RatingsSectionTabs';
import { StudentRankingCard } from '@/pages/Overview/StudentRankingCard';
import { SubjectPicker } from '@/pages/Overview/SubjectPicker';
import { SubjectChartCard } from '@/pages/Overview/SubjectChartCard';
import { EvaluationMode, IconType } from '@/types/components';
import type { EvaluationChartType } from '@/types/ratings';

const TOOLTIP_WIDTH_PX = 315;
const MOBILE_TOOLTIP_WIDTH_PX = 325;

interface Props {
  filters: RatingsFilters;
  evaluationMode: EvaluationMode;
  onEvaluationChange: (
    mode: EvaluationMode,
    lessonTypeId: number | null,
  ) => void;
  onPeriodChange: (period: RatingsFilters['period']) => void;
  onCompareChange: (includeSameLevelClasses: boolean) => void;
  onSameLevelClassesChange: (sameLevelClassIds: number[]) => void;
}

export const RatingsResults: React.FC<Props> = ({
  filters,
  evaluationMode,
  onEvaluationChange,
  onPeriodChange,
  onCompareChange,
  onSameLevelClassesChange,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { periods, arePeriodsLoading } = useStudyYearPeriods();
  const { data = [], isLoading: areRatingsLoading } = useEvaluationRatings({
    filters,
    enabled: true,
  });
  const isLoading = arePeriodsLoading || areRatingsLoading;
  const [selectedType, setSelectedType] = useState<EvaluationChartType | null>(
    null,
  );
  const [selectedRankingType, setSelectedRankingType] =
    useState<EvaluationChartType | null>(null);
  const [selectedSubjectId, setSelectedSubjectId] = useState<number | null>(
    null,
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const availableTypes = data.map(({ type }) => type);
  const selectedChart = findChartByType(data, selectedType);
  const rankingCharts = findChartByType(data, selectedRankingType)?.charts
    .studentAverageEvaluationsCharts;

  const subjects = getRankingSubjects(
    rankingCharts,
    t('overview.ratings.ranking.allSubjects'),
  );
  const selectedSubject =
    subjects.find((subject) => subject.id === selectedSubjectId) ?? subjects[0];
  const rankingChart = getRankingChart(rankingCharts, selectedSubject.id);
  const hasRankingValues = !!rankingChart?.chartValues.length;

  const handleFilterApply = (next: RatingsFilterValues) => {
    onEvaluationChange(next.mode, next.lessonTypeId);
    onPeriodChange(next.period);
    onCompareChange(next.includeSameLevelClasses);
    onSameLevelClassesChange(next.sameLevelClassIds);
    setIsFilterOpen(false);
  };

  const handleRankingTypeChange = (type: EvaluationChartType) => {
    setSelectedRankingType(type);
    setSelectedSubjectId(null);
  };

  const sections = (
    <div className="flex flex-col gap-8 md:gap-10">
      <section className="flex flex-col gap-4">
        <h2 className="text-subhead-14 text-muted-foreground md:text-callout-16 md:text-foreground">
          {t('overview.ratings.gradesTitle')}
        </h2>
        {availableTypes.length > 0 && selectedType && (
          <RatingsSectionTabs
            types={availableTypes}
            value={selectedType}
            onChange={setSelectedType}
          />
        )}
        {!selectedChart && <RatingsEmptyCard />}
        {selectedChart && <SubjectChartCard chart={selectedChart} />}
      </section>

      <section className="flex flex-col">
        <div className="flex items-center gap-2">
          <h2 className="text-subhead-14 text-muted-foreground md:text-callout-16 md:text-foreground">
            {t('overview.ratings.ranking.title')}
          </h2>
          <Tooltip
            content={t('overview.ratings.ranking.tooltip')}
            side={isMobile ? 'bottom' : 'top'}
            maxWidth={isMobile ? MOBILE_TOOLTIP_WIDTH_PX : TOOLTIP_WIDTH_PX}
            isOpen={isTooltipOpen}
            onOpenChange={setIsTooltipOpen}
          >
            <button
              type="button"
              data-tooltip-trigger
              onClick={() => setIsTooltipOpen((previous) => !previous)}
              className="-m-1 flex shrink-0 cursor-pointer items-center justify-center rounded-full p-1 text-subtle-foreground transition-colors hover:bg-hover"
            >
              <Icon icon={IconType.Support} className="size-4" />
            </button>
          </Tooltip>
        </div>
        {availableTypes.length > 0 && selectedRankingType && (
          <div className="mt-4">
            <RatingsSectionTabs
              types={availableTypes}
              value={selectedRankingType}
              onChange={handleRankingTypeChange}
            />
          </div>
        )}
        {rankingCharts && (
          <div className="mt-5 md:mt-4">
            <SubjectPicker
              subjects={subjects}
              value={selectedSubject.id}
              onChange={setSelectedSubjectId}
            />
          </div>
        )}
        <div className="mt-3 md:mt-4">
          {!hasRankingValues && <RatingsEmptyCard />}
          {hasRankingValues && selectedRankingType && (
            <StudentRankingCard
              chart={rankingChart}
              type={selectedRankingType}
            />
          )}
        </div>
      </section>
    </div>
  );

  useCloseOnBreakpointChange(isFilterOpen, () => setIsFilterOpen(false));

  useEffect(() => {
    const types = data.map(({ type }) => type);
    const keepAvailable = (previous: EvaluationChartType | null) =>
      previous && types.includes(previous) ? previous : (types[0] ?? null);

    setSelectedType(keepAvailable);
    setSelectedRankingType(keepAvailable);
  }, [data]);

  return (
    <div className="flex flex-1 flex-col pt-1 md:min-h-0 md:px-6 md:py-6">
      <div className="relative flex flex-1 flex-col rounded-t-2xl bg-popover md:min-h-0 md:rounded-[20px] md:bg-card">
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 px-5 pt-4 pb-3 md:pt-6 md:pb-4">
          <h1 className="text-[16px]/[24px] font-medium text-foreground md:text-[24px]/[32px] md:font-semibold">
            {t('overview.tabs.ratings')}
          </h1>
          <div className="hidden flex-wrap items-center gap-2 md:flex">
            <EvaluationTypePicker
              fitWidth
              isAlignedRight
              mode={evaluationMode}
              lessonTypeId={filters.lessonTypeId}
              onChange={onEvaluationChange}
            />
            <PeriodPicker
              isAlignedRight
              periods={periods}
              value={filters.period}
              onChange={onPeriodChange}
            />
            <CompareChip
              value={filters.includeSameLevelClasses}
              sameLevelClassIds={filters.sameLevelClassIds}
              onChange={onCompareChange}
              onSameLevelClassesChange={onSameLevelClassesChange}
            />
          </div>
          <button
            type="button"
            onClick={() => setIsFilterOpen(true)}
            className="flex cursor-pointer items-center gap-2 rounded-2xl bg-purple-soft px-2 py-1 text-primary md:hidden"
          >
            <FilterSettings className="size-4 shrink-0" />
            <span className="text-caption-12 font-medium">
              {t('overview.ratings.filter')}
            </span>
          </button>
        </div>

        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <PageSpinner className="size-[20px] text-subtle-foreground" />
          </div>
        )}

        {!isLoading && isMobile && (
          <div className="px-5 pt-1 pb-8">{sections}</div>
        )}

        {!isLoading && !isMobile && (
          <AutoHideScrollArea
            hasTopShadow
            hasBottomShadow
            className="min-h-0 flex-1"
            viewportClassName="px-5 pt-2 pb-6"
          >
            {sections}
          </AutoHideScrollArea>
        )}
      </div>

      {isFilterOpen && (
        <RatingsFilterSheet
          values={{
            mode: evaluationMode,
            lessonTypeId: filters.lessonTypeId,
            period: filters.period,
            includeSameLevelClasses: filters.includeSameLevelClasses,
            sameLevelClassIds: filters.sameLevelClassIds,
          }}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleFilterApply}
        />
      )}
    </div>
  );
};
